import { IMdlCustomDialogConfiguration, MdlDialogService } from '@angular-mdl/core';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { ICellRendererParams } from 'ag-grid-community/main';
import { Observable } from 'rxjs';
import { CovanceGridColumn, dateTimeComparator, dateTimeRenderer, dateRenderer, booleanRenderer, booleanQuickFilterText } from '../../covance-ag-grid';
import { AnnouncementEditDialogComponent, ANNOUNCEMENT_EDIT_TOKEN } from './announcement-edit-dialog.component';
import { AnnouncementInfo } from './announcement-info';
import { AnnouncementsActionsMenuAgRendererComponent } from './announcements-actions-menu-ag-renderer.component';
import { AnnouncementsInfoStorage } from './announcements-info-storage';
import { map } from 'rxjs/operators';
import { GridColumns } from '../../shared/grid-columns';
import { UserPermissionsStorage } from '../../user-permissions/user-permissions-storage';
import { UserPermissions } from '../../user-permissions/user-permissions';
import { comparators } from '../../core/comparators';

interface AnnouncementInfoGridRowItem extends ICellRendererParams {
    data: AnnouncementInfo;
}

@Component({
    selector: 'announcements-admin',
    template: require('./announcements-admin.component.html'),
    styles: [require('./announcements-admin.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AnnouncementsAdminComponent {
    public readonly columns$: Observable<CovanceGridColumn[]>;
    public readonly announcementsInfoData$: Observable<AnnouncementInfo[]>;
    public readonly canUserManageAnnouncements$: Observable<boolean>;

    constructor(
        readonly announcementsInfoStorage: AnnouncementsInfoStorage,
        private readonly gridColumns: GridColumns,
        private readonly dialogServce: MdlDialogService,
        readonly userPermissionsStorage: UserPermissionsStorage
    ) {
        this.announcementsInfoData$ = announcementsInfoStorage.all;

        this.canUserManageAnnouncements$ = userPermissionsStorage.userPermissions.pipe(
            map(userPermissions => userPermissions.has('UploadAnnouncements'))
        );

        this.columns$ = userPermissionsStorage.userPermissions.pipe(
            map(userPermissions => this.getColumns(userPermissions))
        );
    }

    public createAnnouncement() {
        const configDialog: IMdlCustomDialogConfiguration = {
            component: AnnouncementEditDialogComponent,
            providers: [{
                provide: ANNOUNCEMENT_EDIT_TOKEN, useValue: null
            }],
            isModal: true,
            clickOutsideToClose: true
        };

        this.dialogServce.showCustomDialog(configDialog);
    }

    private getColumns(userPermissions: UserPermissions): CovanceGridColumn[] {
        const columns: CovanceGridColumn[] = [
            {
                headerName: 'Title',
                field: 'title',
                comparator: (a, b) => comparators.mixedStringComparator(a, b)
            },
            this.gridColumns.multiLineStringColumn({
                title: 'Roles',
                field: 'roles',
                stringsValueGetter: (entity: AnnouncementInfo) => entity.roles,
                tooltipGetter: (role) => [{ content: role }]
            }),
            {
                headerName: 'Pinned?',
                field: 'pinnedFlag',
                cellRenderer: booleanRenderer,
                getQuickFilterText: booleanQuickFilterText,
            },
            {
                headerName: 'Start Date',
                field: 'effectiveStartDate',
                comparator: dateTimeComparator,
                valueGetter: (params: AnnouncementInfoGridRowItem) => {
                    if (!params.data) {
                        return null;
                    }
                    return dateRenderer({ value: params.data.effectiveStartDate });
                }
            },
            {
                headerName: 'End Date',
                field: 'effectiveEndDate',
                comparator: dateTimeComparator,
                valueGetter: (params: AnnouncementInfoGridRowItem) => {
                    if (!params.data) {
                        return null;
                    }
                    return dateRenderer({ value: params.data.effectiveEndDate });
                }
            },
            {
                headerName: 'Posted',
                field: 'posted',
                sort: 'desc',
                comparator: dateTimeComparator,
                valueGetter: (params: AnnouncementInfoGridRowItem) => {
                    if (!params.data) {
                        return null;
                    }
                    return dateTimeRenderer({ value: params.data.posted });
                }
            },
        ];

        this.appendActionsMenu(userPermissions, columns);

        return columns;
    }

    private appendActionsMenu(userPermissions: UserPermissions, columns: CovanceGridColumn[]) {
        if (userPermissions.has('UploadAnnouncements')) {
            columns.push(
                this.gridColumns.actionsMenuColumn({ cellRendererFramework: AnnouncementsActionsMenuAgRendererComponent })
            );
        }
    }
}